import { Logger } from '@nextgen-web-framework/core';
import { handleRequest } from '../../transformers';
import { type CustomRequestOptions, ProjectionType } from '../../types';
import { CategoryTreeIdType } from '../../types/enums/categoryEnums';
import { logObject } from '../../utils';
import { getCategoriesByIdsReqObj, getCategoriesReqObj, getCategorytReqObj } from '../config/categoryEndPoints';
import type { Category, CategoryFullResponse, ResponseCategories } from '../models';

const API_VERSION_V1 = 'v1';

type ICategoryAPI = {
  categoryId: string;
  projections?: string;
  treeId?: string;
};

type ICategoriesAPI = { treeId: string; projections: string };

const loggerInstance = Logger.getInstance(logObject('categoryApi'));

export const categoryApi = {
  async getCategoryById(
    { categoryId, projections = ProjectionType.Cat, treeId = CategoryTreeIdType.Cat }: ICategoryAPI,
    options: CustomRequestOptions = {},
  ): Promise<Category | Response> {
    try {
      if (!categoryId) {
        loggerInstance.error('Missing category ID. Please check arguments for a valid category ID.');
        throw new Error('Null argument. Please retry with valid category id.');
      }

      const requestObj = getCategorytReqObj(API_VERSION_V1, categoryId);
      let errorResponse: Response | undefined;

      requestObj.params = {
        projections,
        treeId,
      };

      options.errorHandlers = {
        default: (res: Response) => {
          errorResponse = res;
        },
      };

      const response = await handleRequest<Category>(requestObj, options);
      return errorResponse ? errorResponse : response;
    } catch (err) {
      loggerInstance.error(`Failed to create category request object. Message: ${err}`);
      throw err;
    }
  },

  async getCategories(
    payload: ICategoriesAPI,
    versionId: string = API_VERSION_V1,
    options?: CustomRequestOptions,
  ): Promise<ResponseCategories> {
    try {
      if (!payload?.treeId) {
        loggerInstance.error('Missing tree ID. Please check arguments for a valid tree ID.');
        throw new Error('Null argument. Please retry with valid tree id.');
      }

      if (!payload?.projections) {
        loggerInstance.error('Missing projections. Please check arguments for a valid projections.');
        throw new Error('Null argument. Please retry with valid projections.');
      }

      const requestObj = getCategoriesReqObj(versionId);
      requestObj.params = {
        ...payload,
      };
      return await handleRequest<ResponseCategories>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create category request object. Message: ${err}`);
      throw err;
    }
  },

  async getCategoriesByIds(
    catIds: string[] = [],
    versionId: string = API_VERSION_V1,
    projections = ProjectionType.Min,
    options?: CustomRequestOptions,
    treeId?: string,
  ): Promise<ResponseCategories> {
    try {
      if (catIds?.length === 0) {
        loggerInstance.error('Missing category ids. Please check arguments for a valid category ids.');
        throw new Error('Null argument. Please retry with valid category ids.');
      }

      const requestObj = getCategoriesByIdsReqObj(versionId);
      requestObj.params = {
        projections,
        catids: catIds.toString(),
        treeId: treeId?.toString() || CategoryTreeIdType.Cat,
      };
      return await handleRequest<ResponseCategories>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create category request object. Message: ${err}`);
      throw err;
    }
  },
};

export default categoryApi;
export type { CategoryFullResponse, ICategoryAPI };
